import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, LocPortal, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Credentials|Fulfillment|Address Confirmation';
const sharedProps = {
	htmlAllowed: false,
	shared: false,
	portal: 'CP' as LocPortal,
	feature: HubFeature.Credential,
};

export type AddressConfirmationLoc =
	| 'CPAddressConfirmationTitle'
	| 'CPAddressConfirmationUpdateButton'
	| 'CPAddressConfirmationConfirmButton'
	| 'CPAddressConfirmationConfirmedButton'
	| 'CPAddressConfirmationCancelButton'
	| 'CPAddressConfirmationMailingAddressTitle'
	| 'CPAddressConfirmationLocalizedAddressTitle'
	| 'CPAddressConfirmationCountryLabel'
	| 'CPAddressConfirmationAddressLabel'
	| 'CPAddressConfirmationAddress2Label'
	| 'CPAddressConfirmationAddress3Label'
	| 'CPAddressConfirmationCityLabel'
	| 'CPAddressConfirmationStateLabel'
	| 'CPAddressConfirmationPostalCodeLabel'
	| 'CPAddressConfirmationPhoneLabel'
	| 'CPAddressConfirmationBusinessCheckbox'
	| 'CPAddressConfirmationSubmitButton'
	| 'CPAddressConfirmationInvalidCountryError'
	| 'CPAddressConfirmationInvalidStateError'
	| 'CPAddressConfirmationPostalCodeUSAError'
	| 'CPAddressConfirmationPostalCodeCanadaError'
	| 'CPAddressConfirmationPhoneFormatError'
	| 'CPAddressConfirmationPhoneZerosError'
	| 'CPAddressConfirmationRequiredFieldsMissing'
	| 'CPAddressConfirmationAddressRequiredError'
	| 'CPAddressConfirmationCityRequiredError'
	| 'CPAddressConfirmationStateRequiredError'
	| 'CPAddressConfirmationStateRequiredVueHint'
	| 'CPAddressConfirmationStateInvalidError'
	| 'CPAddressConfirmationPostalCodeRequiredError'
	| 'CPAddressConfirmationPhoneRequiredError'
	| 'CPAddressConfirmationPhoneUsaCanadaError'
	| 'CPAddressConfirmationCountryRequiredError'
	| 'CPAddressConfirmationBusinessCheckboxLabel'
	| 'CPAddressConfirmationLocalizedAddressTitle'
	| 'CPAddressConfirmationAddressUpdatedMessage'
	| 'CPAddressConfirmationFormCountryHint';

export const cpAddressConfirmationLocs: LocDefs<AddressConfirmationLoc> = {
	CPAddressConfirmationTitle: {
		defaultValue: 'Mailing address',
		displayType: 'Title',
		description: 'Title for the address confirmation section',
		displayPath: `${dpRoot}|Title`,
		legacyName:
			'Credential.Fulfillment/Credentials.FulfillmentHistory.Title.AddressConfirmation',
		...sharedProps,
	},
	CPAddressConfirmationUpdateButton: {
		defaultValue: 'Update and Confirm Address',
		displayType: 'Button',
		description: 'Button text to update the address',
		displayPath: `${dpRoot}|Update Button`,
		legacyName: 'Credential.Fulfillment/Credentials.FulfillmentHistory.Button.UpdateAddress',
		...sharedProps,
	},
	CPAddressConfirmationConfirmButton: {
		defaultValue: 'Confirm address',
		displayType: 'Button',
		description: 'Button text to confirm the address',
		displayPath: `${dpRoot}|Confirm Button`,
		legacyName: 'Credential.Fulfillment/Credentials.FulfillmentHistory.Button.ConfirmAddress',
		...sharedProps,
	},
	CPAddressConfirmationConfirmedButton: {
		defaultValue: 'Confirmed',
		displayType: 'Button',
		description: 'Button text when the address is confirmed',
		displayPath: `${dpRoot}|Confirmed Button`,
		legacyName: 'Credential.Fulfillment/Credentials.FulfillmentHistory.Button.ConfirmAddress2',
		...sharedProps,
	},
	CPAddressConfirmationCancelButton: {
		defaultValue: 'Cancel',
		displayType: 'Button',
		description: 'Button text to cancel the address update',
		displayPath: `${dpRoot}|Cancel Button`,
		legacyName: 'Credential.Fulfillment/Credentials.FulfillmentHistory.Button.Cancel',
		...sharedProps,
	},
	CPAddressConfirmationMailingAddressTitle: {
		defaultValue: 'Mailing Address',
		displayType: 'Title',
		description: 'Title for the mailing address form section',
		displayPath: `${dpRoot}|Mailing Address Title`,
		legacyName: 'Credential.Fulfillment/Credentials.FulfillmentHistory.Title.MailingAddress',
		...sharedProps,
	},
	CPAddressConfirmationLocalizedAddressTitle: {
		defaultValue: 'Localized Address',
		displayType: 'Title',
		description: 'Title for the localized address form section',
		displayPath: `${dpRoot}|Localized Address Title`,
		legacyName:
			'CandidateProfile.Addresses.Localized/Credentials.FulfillmentHistory.Title.LocalizedAddress',
		...sharedProps,
	},
	CPAddressConfirmationCountryLabel: {
		defaultValue: 'Country',
		displayType: 'Label',
		description: 'Label for the country field',
		displayPath: `${dpRoot}|Country Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Country',
		...sharedProps,
	},
	CPAddressConfirmationAddressLabel: {
		defaultValue: 'Address',
		displayType: 'Label',
		description: 'Label for the address field',
		displayPath: `${dpRoot}|Address Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Address',
		...sharedProps,
	},
	CPAddressConfirmationAddress2Label: {
		defaultValue: 'Address (cont.)',
		displayType: 'Label',
		description: 'Label for the second address field',
		displayPath: `${dpRoot}|Address 2 Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Address2',
		...sharedProps,
	},
	CPAddressConfirmationAddress3Label: {
		defaultValue: 'Address (cont.)',
		displayType: 'Label',
		description: 'Label for the third address field',
		displayPath: `${dpRoot}|Address 3 Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Address3',
		...sharedProps,
	},
	CPAddressConfirmationCityLabel: {
		defaultValue: 'City',
		displayType: 'Label',
		description: 'Label for the city field',
		displayPath: `${dpRoot}|City Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.City',
		...sharedProps,
	},
	CPAddressConfirmationStateLabel: {
		defaultValue: 'State/Province',
		displayType: 'Label',
		description: 'Label for the state/province field',
		displayPath: `${dpRoot}|State Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.State',
		...sharedProps,
	},
	CPAddressConfirmationPostalCodeLabel: {
		defaultValue: 'Zip/Postal Code',
		displayType: 'Label',
		description: 'Label for the postal code field',
		displayPath: `${dpRoot}|Postal Code Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.PostalCode',
		...sharedProps,
	},
	CPAddressConfirmationPhoneLabel: {
		defaultValue: 'Phone',
		displayType: 'Label',
		description: 'Label for the phone field',
		displayPath: `${dpRoot}|Phone Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Phone',
		...sharedProps,
	},
	CPAddressConfirmationPhoneRequiredError: {
		defaultValue: 'Phone is required',
		displayType: 'Error',
		description: 'Error message for required phone field',
		displayPath: `${dpRoot}|Phone Required Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneRequired',
		...sharedProps,
	},
	CPAddressConfirmationCountryRequiredError: {
		defaultValue: 'Country is required',
		displayType: 'Error',
		description: 'Error message for required countryfield',
		displayPath: `${dpRoot}|Country Required Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.CountryRequired',
		...sharedProps,
	},

	CPAddressConfirmationPhoneUsaCanadaError: {
		defaultValue: 'Phone number must have 10 numbers',
		displayType: 'Error',
		description: 'Error message for invalid USA/Canada phone number format',
		displayPath: `${dpRoot}|Phone USA/Canada Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneUsa',
		...sharedProps,
	},
	CPAddressConfirmationPhoneZerosError: {
		defaultValue: 'Phone number must not be all zeros',
		displayType: 'Error',
		description: 'Error message for phone number with all zeros',
		displayPath: `${dpRoot}|Phone Zeros Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneZeros',
		...sharedProps,
	},
	CPAddressConfirmationBusinessCheckbox: {
		defaultValue: 'This is a Business Address',
		displayType: 'Label',
		description: 'Label for the business address checkbox',
		displayPath: `${dpRoot}|Business Address Checkbox`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Business',
		...sharedProps,
	},
	CPAddressConfirmationSubmitButton: {
		defaultValue: 'Update and Confirm Addresses',
		displayType: 'Button',
		description: 'Button text to submit the address form',
		displayPath: `${dpRoot}|Submit Button`,
		legacyName: 'Credential.Fulfillment/Credentials.FulfillmentHistory.Button.UpdateAddress',
		...sharedProps,
	},
	CPAddressConfirmationAddressRequiredError: {
		defaultValue: 'Address is required',
		displayType: 'Error',
		description: 'Error message for required fields',
		displayPath: `${dpRoot}|Required Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.AddressRequired',
		...sharedProps,
	},
	CPAddressConfirmationStateRequiredError: {
		defaultValue: 'State/Province is required',
		displayType: 'Error',
		description: 'Error message for required state/province field',
		displayPath: `${dpRoot}|State Required Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.StateRequired',
		...sharedProps,
	},
	CPAddressConfirmationStateRequiredVueHint: {
		defaultValue: 'State/Province is required. If your state is not listed, please type N/A',
		displayType: 'Error',
		description: 'Error with Vue specific hint for state required.',
		displayPath: `${dpRoot}|State Required Vue Hint`,
		htmlAllowed: false,
		shared: false,
		portal: 'CP' as LocPortal,
		feature: HubFeature.CandidateAccessIntegrationEgressPearsonVue,
	},

	CPAddressConfirmationStateInvalidError: {
		defaultValue: 'Invalid state/province',
		displayType: 'Error',
		description: 'Error message for invalid state/province selection',
		displayPath: `${dpRoot}|Invalid State Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.StateInvalid',
		...sharedProps,
	},
	CPAddressConfirmationInvalidCountryError: {
		defaultValue: 'Invalid country',
		displayType: 'Error',
		description: 'Error message for invalid country selection',
		displayPath: `${dpRoot}|Invalid Country Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.InvalidCountry',
		...sharedProps,
	},
	CPAddressConfirmationInvalidStateError: {
		defaultValue: 'Invalid state/province',
		displayType: 'Error',
		description: 'Error message for invalid state/province selection',
		displayPath: `${dpRoot}|Invalid State Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.StateInvalid',
		...sharedProps,
	},
	CPAddressConfirmationPostalCodeRequiredError: {
		defaultValue: 'Zip/Postal Code is required',
		displayType: 'Error',
		description: 'Error message for required postal code field',
		displayPath: `${dpRoot}|Postal Code Required Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalRequired',
		...sharedProps,
	},
	CPAddressConfirmationPostalCodeUSAError: {
		defaultValue:
			'Zip/Postal Code must follow one of the two following formats: #####, #####-####',
		displayType: 'Error',
		description: 'Error message for invalid USA postal code format',
		displayPath: `${dpRoot}|USA Postal Code Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalUsa',
		...sharedProps,
	},
	CPAddressConfirmationPostalCodeCanadaError: {
		defaultValue: 'Zip/Postal Code must follow the following format: A#A#A#',
		displayType: 'Error',
		description: 'Error message for invalid Canada postal code format',
		displayPath: `${dpRoot}|Canada Postal Code Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PostalCanada',
		...sharedProps,
	},
	CPAddressConfirmationPhoneFormatError: {
		defaultValue: 'Phone number must have 10 numbers',
		displayType: 'Error',
		description: 'Error message for invalid phone number format',
		displayPath: `${dpRoot}|Phone Format Error`,
		legacyName:
			'CandidateAccess.Integration.Egress.PearsonVue/CandidateProfile.Addresses.FieldError.PhoneUsa',
		...sharedProps,
	},
	CPAddressConfirmationRequiredFieldsMissing: {
		defaultValue: 'Required fields in the address are missing. Please update your address.',
		displayType: 'Notification',
		description: 'Error message for phone number with all zeros',
		displayPath: `${dpRoot}|Phone Zeros Error`,
		legacyName:
			'Credential.Fulfillment/Credentials.FulfillmentHistory.Notification.RequiredFieldsMissing',
		...sharedProps,
	},
	CPAddressConfirmationCityRequiredError: {
		defaultValue: 'City is required',
		displayType: 'Error',
		description: 'Error message for required city field',
		displayPath: `${dpRoot}|City Required Error`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldError.CityRequired',
		...sharedProps,
	},
	CPAddressConfirmationBusinessCheckboxLabel: {
		defaultValue: 'This is a Business Address',
		displayType: 'Label',
		description: 'Label for the business address checkbox',
		displayPath: `${dpRoot}|Business Address Checkbox Label`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.FieldLabel.Business',
		...sharedProps,
	},
	CPAddressConfirmationAddressUpdatedMessage: {
		defaultValue: 'Changes saved',
		displayType: 'Notification',
		description: 'Notification for successful address update',
		displayPath: `${dpRoot}|Address Updated Notification`,
		legacyName: 'CandidateProfile.Addresses/Profile.Addresses.NotificationMessage.Success',
		...sharedProps,
	},
	CPAddressConfirmationFormCountryHint: {
		defaultValue: 'To change your country, please contact support.',
		displayType: 'Content',
		description: 'Hint for country.',
		displayPath: `${dpRoot}|Country Hint`,
		feature: HubFeature.CandidateProfileRestrictCandidateCountryUpdates,
		htmlAllowed: true,
		shared: false,
		portal: 'CP' as LocPortal,
	},
};

export const LOC_KEYS = MapLocKeys<AddressConfirmationLoc>(cpAddressConfirmationLocs);
export type CpAddressConfirmationLocType = LocMap<AddressConfirmationLoc>;
